import { SectionShell } from "@/components/design-system/SectionShell";
import { Spinner, ProgressBar } from "@/components/ui/progress";

const code = `import { Spinner, ProgressBar } from "@/components/ui/progress";

// Indeterminate wait
<Spinner />                     {/* sm (default) */}
<Spinner size="md" label="Processing plan" />

// Determinate progress
<ProgressBar value={38} />
<p className="text-xs text-ink-muted">12 / 32 pages</p>`;

export function ProgressSection() {
  return (
    <SectionShell
      id="progress"
      title="Progress"
      description={
        <>
          Feedback for background work. <code>&lt;Spinner&gt;</code> is the
          indeterminate case — a subtle spinning icon for "something is
          happening". <code>&lt;ProgressBar&gt;</code> is the determinate case —
          pass <code>value</code> (0–100) and pair it with a short muted count
          ("12 / 32 pages") so the number does the talking.
        </>
      }
      whenToUse={
        <ul>
          <li>Background jobs the user is waiting on — uploads, plan processing, tiling.</li>
          <li>Inline in list rows or cards next to the thing that is busy.</li>
          <li>ProgressBar whenever real progress is known; Spinner only when it isn't.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Full-page loading — prefer page-level skeleton copy (see the editor's loading state).</li>
          <li>Instant actions — a disabled Button with its normal label is enough.</li>
        </ul>
      }
      preview={
        <div className="max-w-sm space-y-6">
          <div className="flex items-center gap-3">
            <Spinner />
            <span className="text-sm text-ink-muted">Queued…</span>
          </div>
          <div className="space-y-2">
            <ProgressBar value={38} />
            <p className="text-xs text-ink-muted">12 / 32 pages</p>
          </div>
          <div className="flex items-center gap-3">
            <Spinner size="md" label="Processing" />
            <span className="text-sm text-ink-muted">Tiling "1st Floor"…</span>
          </div>
        </div>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li><code>Spinner size</code>: <code>sm</code> (16px, default) or <code>md</code> (24px); <code>label</code> feeds a screen-reader-only text node.</li>
          <li><code>ProgressBar value</code> clamps to 0–100 and animates width changes; it exposes <code>role="progressbar"</code> with aria values.</li>
          <li>Keep bars thin (<code>h-1.5</code> track) — they annotate a row, they aren't the row.</li>
        </ul>
      }
    />
  );
}
